import React, { useEffect, useMemo, useRef, useState } from 'react';
import { INDIA_STATES, INDIA_VIEWBOX, stateKey } from './indiaStatePaths';

const NO_DATA_FILL = '#EFE9D8';
const ACTIVE_FILL = 'rgba(184,230,210,0.45)';
const PENDING_FILL = '#EFE6C8';
const TEST_RAMP = ['#B8E6D2', '#AED252', '#145A32', '#073F2A'];
const LAYERS = [
  { key: 'tests', label: 'Soil tests' },
  { key: 'score', label: 'Soil score gain' },
];

const fmt = (n) => Math.round(n).toLocaleString('en-IN');

/** Up to four soil-test ranges scaled to this partner's busiest state. */
const buildTestBuckets = (max) => {
  if (max <= 0) return [];
  const highs = [...new Set([Math.ceil(max / 4), Math.ceil(max / 2), Math.ceil((3 * max) / 4), max])];
  const colors = TEST_RAMP.slice(TEST_RAMP.length - highs.length);
  let lo = 1;
  return highs.map((hi, i) => {
    const bucket = { lo, hi, color: colors[i], label: lo === hi ? fmt(lo) : `${fmt(lo)}–${fmt(hi)}` };
    lo = hi + 1;
    return bucket;
  });
};

/** Square viewBox around a bounding box, as in the design (state zoom). */
const fitBox = (b, mult, min) => {
  const m = Math.max(min, Math.max(b.width, b.height) * mult);
  return [b.x + b.width / 2 - m / 2, b.y + b.height / 2 - m / 2, m, m];
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

/**
 * India map of the partner's state coverage.
 * coverage: Map(stateKey → active_states row). scopeKey: 'all' or a stateKey.
 */
const IndiaStateMap = ({ coverage, scopeKey, layer, onLayerChange, onSelectState, onReset, unspecifiedFarmers }) => {
  const pathRefs = useRef({});
  const [viewBox, setViewBox] = useState(INDIA_VIEWBOX);
  const viewBoxRef = useRef(INDIA_VIEWBOX);
  const [labelBox, setLabelBox] = useState(null);

  const zoomed = scopeKey !== 'all';
  const selectedIsNoData = zoomed && !coverage.has(scopeKey);

  // Zoom to the selected state (animated unless reduced motion is preferred).
  useEffect(() => {
    let target = INDIA_VIEWBOX;
    let box = null;
    const el = zoomed ? pathRefs.current[scopeKey] : null;
    if (el && typeof el.getBBox === 'function') {
      try {
        box = el.getBBox();
        target = fitBox(box, selectedIsNoData ? 1.5 : 1.3, selectedIsNoData ? 90 : 120);
      } catch (e) {
        box = null;
      }
    }
    setLabelBox(box);

    const from = viewBoxRef.current;
    if (prefersReducedMotion() || typeof window.requestAnimationFrame !== 'function') {
      viewBoxRef.current = target;
      setViewBox(target);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / 600);
      const e = 1 - Math.pow(1 - t, 3);
      const vb = from.map((v, i) => v + (target[i] - v) * e);
      viewBoxRef.current = vb;
      setViewBox(vb);
      if (t < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [scopeKey, zoomed, selectedIsNoData]);

  const buckets = useMemo(
    () => buildTestBuckets(Math.max(0, ...[...coverage.values()].map((s) => s.soil_tests))),
    [coverage]
  );

  const fillFor = (key) => {
    if (key === scopeKey && selectedIsNoData) return '#F3E6C4';
    const row = coverage.get(key);
    if (!row) return NO_DATA_FILL;
    if (layer === 'score') return PENDING_FILL;
    const bucket = buckets.find((b) => row.soil_tests >= b.lo && row.soil_tests <= b.hi);
    return bucket ? bucket.color : ACTIVE_FILL;
  };

  const legend = layer === 'score'
    ? { title: 'Soil health score gain (points)', items: [{ color: PENDING_FILL, label: '0 · data pending' }, { color: NO_DATA_FILL, label: 'No data' }] }
    : {
      title: 'Soil tests per state',
      items: [
        { color: ACTIVE_FILL, label: 'Active · no tests yet' },
        ...buckets.map((b) => ({ color: b.color, label: b.label })),
        { color: NO_DATA_FILL, label: 'No data' },
      ],
    };

  const u = viewBox[3] / 580; // keeps strokes and labels a constant on-screen size while zoomed
  const selectedName = zoomed ? INDIA_STATES.find((s) => s.d && stateKey(s.name) === scopeKey)?.name : null;

  const handleKey = (key) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelectState(key);
    }
  };

  return (
    <section aria-labelledby="csr-map-h" className="csr-card csr-map">
      <div className="csr-map__head">
        <div className="d-flex flex-column" style={{ gap: 2 }}>
          <h2 id="csr-map-h">State coverage across India</h2>
          <p>Highlighted states are where this partner has farmers or soil tests. Select a state on the map or in the State menu to see its districts.</p>
        </div>
        <div role="group" aria-label="Map layer" className="csr-seg">
          {LAYERS.map((l) => (
            <button key={l.key} type="button" aria-pressed={layer === l.key} onClick={() => onLayerChange(l.key)}>{l.label}</button>
          ))}
        </div>
      </div>

      <div className="csr-map__stage">
        <svg
          className="csr-map__svg"
          viewBox={viewBox.map((n) => n.toFixed(1)).join(' ')}
          role="group"
          aria-label="Map of India showing the states where this partner is active"
        >
          {INDIA_STATES.filter((s) => s.d).map((s) => {
            const key = stateKey(s.name);
            const row = coverage.get(key);
            const selected = key === scopeKey;
            const dimmed = zoomed && !selected && Boolean(row);
            return (
              <path
                key={s.slug}
                ref={(el) => { pathRefs.current[key] = el; }}
                className="csr-map__state"
                d={s.d}
                fill={fillFor(key)}
                fillOpacity={dimmed ? 0.5 : 1}
                stroke={selected ? (selectedIsNoData ? '#D8A63A' : '#101510') : '#FFFDF7'}
                strokeWidth={(selected ? 2.2 : 1) * u}
                strokeLinejoin="round"
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-label={row ? `${s.name}: ${fmt(row.soil_tests)} soil tests, ${fmt(row.farmers)} farmers` : `${s.name}: no data yet`}
                onClick={() => onSelectState(key)}
                onKeyDown={handleKey(key)}
              >
                <title>{s.name}</title>
              </path>
            );
          })}
          {selectedName && labelBox ? (
            <text
              x={labelBox.x + labelBox.width / 2}
              y={labelBox.y + labelBox.height / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={16 * u}
              fontWeight="700"
              fill={selectedIsNoData ? '#5E4214' : '#101510'}
              stroke="#FFFDF7"
              strokeWidth={3 * u}
              strokeLinejoin="round"
              style={{ paintOrder: 'stroke' }}
              pointerEvents="none"
            >
              {selectedName}
            </text>
          ) : null}
        </svg>

        {zoomed ? (
          <button type="button" className="csr-btn csr-btn--ghost csr-map__back" onClick={onReset}>← All India</button>
        ) : null}

        <div className="csr-map__legend">
          <span className="csr-map__legend-title">{legend.title}</span>
          <div className="csr-map__legend-items">
            {legend.items.map((it) => (
              <span key={it.label} className="csr-map__legend-item">
                <span className="csr-map__swatch" style={{ background: it.color }} />{it.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <p className="csr-map__foot">
        {unspecifiedFarmers > 0 ? `${fmt(unspecifiedFarmers)} farmers have no recorded state and are not shown on the map. ` : ''}
        Grey states have no data for this partner yet. Boundaries are simplified; verify against Survey of India maps before external use.
      </p>
    </section>
  );
};

export default IndiaStateMap;